import { t } from 'i18next';
import _ from 'lodash';
import { sqScalingModelStore } from '@/core/core.stores';
import { validateFormula } from '@/formula/formula.utilities';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { sqModelsApi } from '@/sdk/api/ModelsApi';
import { ColumnTypeEnum } from '@/sdk/model/ColumnDefinitionInputV1';
import { OutputTypeEnum } from '@/sdk/model/InputFeatureV1';
import { DEFAULT_ITEM_ID_COLUMN_NAME } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';
import {
  getExistingTableDefinition,
  setColumnIdToBeUsedAsModelInput,
  setDoTableReload,
  setIsApplyingModel,
  setModelInputToSelect,
  setSelectedColumns,
} from '@/tableDefinitionEditor/tableDefinition.actions';
import { EDITOR_TYPES, TABLE_DEFINITION_COLUMN_OPTIONS } from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  setColumnToEditId,
  setSelectedColumnOption,
  setShowColumnProperties,
} from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { addOrUpdateColumn } from '@/tableDefinitionEditor/tableGridColumns.utilities';
import { closeInvestigationTool, setActiveTool } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { getModelInputFeatures, resetModel, setModel, setModelOutput } from '@/tools/models/model.actions';
import { MODEL_TYPES } from '@/tools/models/model.types';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { decorate } from '@/trend/trendViewer/itemDecorator.utilities';
import { addTrendItem, clearModelProps, clearRetiredModelsProps, getModelProps } from '@/trendData/trend.actions';
import { ITEM_DATA_STATUS, ITEM_TYPES } from '@/trendData/trendData.constants';
import { getDependencies } from '@/utilities/formula.utilities';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { getNextName } from '@/utilities/utilities';
import { fetchModels } from '@/workbook/workbook.actions';
import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { FormulaEditorParam } from '@/formula/FormulaParametersTable.molecule';
import type { ColumnDefinitionInputV1 } from '@/sdk/model/ColumnDefinitionInputV1';
import type { DerivedFeatureV1 } from '@/sdk/model/DerivedFeatureV1';
import type { InputFeatureV1 } from '@/sdk/model/InputFeatureV1';
import type { InstanceOutputMappingV1 } from '@/sdk/model/InstanceOutputMappingV1';
import type { InstanceOutputV1 } from '@/sdk/model/InstanceOutputV1';
import type { ModelApplicationMappingV1 } from '@/sdk/model/ModelApplicationMappingV1';
import type { ModelFromIdsInputV1 } from '@/sdk/model/ModelFromIdsInputV1';
import type { ModelInitialApplicationInputV1 } from '@/sdk/model/ModelInitialApplicationInputV1';
import type { ModelReapplicationInputV1 } from '@/sdk/model/ModelReapplicationInputV1';
import type { ModelRuleInputV1Object } from '@/sdk/model/ModelRuleInputV1Object';
import type { ModelTableApplicationsOutputListV1 } from '@/sdk/model/ModelTableApplicationsOutputListV1';
import type { ModelTableApplicationsOutputV1 } from '@/sdk/model/ModelTableApplicationsOutputV1';
import type { ReferencedOutputItemV1 } from '@/sdk/model/ReferencedOutputItemV1';
import type { ModelOutputV1 } from '@/sdk/model/models';
import type { ColumnDefinition, EditorType, ModelToSelect } from '@/tableDefinitionEditor/tableDefinition.types';
import type { FormulaItemTableOption } from '@/tools/formula/FormulaItemTable.atom';
import type { ModelInput, ModelOutput } from '@/tools/models/model.store';
import type {
  ModelType,
  ModelVersionMetadata,
  NewlyAddedDerivedFeature,
  OutputDependencyItem,
} from '@/tools/models/model.types';

/**
 * Type guard to check if a given feature is a ColumnDefinition.
 *
 * @param feature - The feature to check
 * @returns True if the feature is a ColumnDefinition, false otherwise
 */
export const isFeatureTypeColumnDefinition = (feature: unknown): feature is ColumnDefinition => {
  if (!feature || typeof feature !== 'object') {
    return false;
  }
  return 'columnName' in feature;
};

/**
 * Creates a new model in the backend from a model definition derived from column definitions.
 */
export const createModelFromColumnDefinitions = async (model: ModelFromIdsInputV1) => {
  const { data: modelOutput } = await sqModelsApi.createModelFromTableColumnDefinitions(model);
  return modelOutput;
};

/**
 * Merges version/draft metadata into model items. Pure function --
 * takes a list of models and a metadata map, returns models with
 * `versionMetadata` attached where available.
 */
export const mergeVersionMetadata = <T extends { id: string }>(
  models: T[],
  metadataMap: Map<string, ModelVersionMetadata>,
): T[] =>
  models.map((m) => {
    const metadata = metadataMap.get(m.id);
    return metadata ? { ...m, versionMetadata: metadata } : m;
  });

/** Fetches a model by ID and sets it in the appropriate model store (Scaling or Tool) */
export const fetchModel = async (
  id: string,
  modelType: ModelType,
  saveState = true,
  isEditing = false,
  mappings: ModelApplicationMappingV1[] | undefined = undefined,
) => {
  try {
    const { data: model } = await sqModelsApi.getModel({ modelId: id });
    if (model?.inputFeatures) {
      model.inputFeatures = model.inputFeatures.map((input: InputFeatureV1) => {
        const updatedInput = { ...input } as InputFeatureV1 & {
          isCollapsed?: boolean;
          columnDefinition?: ColumnDefinition;
        };
        if (isEditing) {
          updatedInput.isCollapsed = true;
        }
        if (mappings) {
          const mapping = mappings.find((m) => m.featureId === input.id);
          if (mapping && mapping.columnId) {
            updatedInput.columnDefinition = { id: mapping.columnId } as unknown as ColumnDefinition;
          }
        }
        return updatedInput;
      });
    }
    if (saveState) {
      setModelOutput(modelType, model);
    }
    return model;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

export const findUniqueColumnNames = (tableColumnNames: string[], oldColumnNames: string[]) => {
  const newColumnNames: string[] = [];
  const duplicateOutputNames = getDuplicateOutputNames(tableColumnNames, oldColumnNames);
  oldColumnNames.forEach((name) => {
    if (duplicateOutputNames.find((duplicatedName) => duplicatedName === name)) {
      const prefix = `${name}`;
      newColumnNames.push(getNextName([...tableColumnNames, ...newColumnNames], prefix, true, true));
    } else {
      newColumnNames.push(name);
    }
  });

  return newColumnNames;
};

export const updateDerivedFeature = (
  patch: Partial<ModelOutput | ColumnDefinition | DerivedFeatureV1>,
  index: number,
  derivedFeatures: ColumnDefinition[] | ModelOutput[] | DerivedFeatureV1[],
  modelType: ModelType,
) => {
  const updatedFeature: ColumnDefinition | ModelOutput | DerivedFeatureV1 = { ...derivedFeatures[index], ...patch } as
    | ColumnDefinition
    | ModelOutput
    | DerivedFeatureV1;
  const updatedDerivedFeatures = derivedFeatures.map((feature, i) => (i === index ? updatedFeature : feature)) as
    | ColumnDefinition[]
    | ModelOutput[]
    | DerivedFeatureV1[];
  setModel(modelType, { derivedFeatures: updatedDerivedFeatures });
};

/**
 * Recursively finds all input and intermediate column IDs for a given starting column ID.
 * Only includes DEFAULT_ITEM_ID_COLUMN_NAME if it is a direct input to an output.
 */
export const getInputAndIntermediateIds = (
  startId: string,
  visitedIds: string[],
  inputs: string[],
  intermediates: string[],
  outputIds: string[],
  columnIdMap: Map<string, ColumnDefinition>,
  isDirectInputToOutput = false,
) => {
  if (visitedIds.includes(startId)) {
    return;
  }
  visitedIds.push(startId);

  const col = columnIdMap.get(startId);
  if (!col) {
    return;
  }

  const directInputs: string[] = [];
  const isSearchItemsDisplayName = (name: string) => name === DEFAULT_ITEM_ID_COLUMN_NAME;
  col.rules?.forEach((r) => r.inputs?.forEach((inp) => directInputs.push(inp)));

  if (directInputs.length === 0) {
    // Only include DEFAULT_ITEM_ID_COLUMN_NAME if it's a direct input to an output
    if (
      !inputs.includes(startId) &&
      !outputIds.includes(startId) &&
      (!isSearchItemsDisplayName(col.displayName) || isDirectInputToOutput)
    ) {
      inputs.push(startId);
    }
    return;
  }

  if (
    !isValidOutputColumn(col) &&
    !inputs.includes(startId) &&
    (!isSearchItemsDisplayName(col.displayName) || isDirectInputToOutput)
  ) {
    inputs.push(startId);
  } else if (!outputIds.includes(startId) && !intermediates.includes(startId) && isValidOutputColumn(col)) {
    intermediates.push(startId);
  }

  // For each direct input, pass true if the current column is an output
  directInputs.forEach((depId) =>
    getInputAndIntermediateIds(
      depId,
      visitedIds,
      inputs,
      intermediates,
      outputIds,
      columnIdMap,
      outputIds.includes(startId),
    ),
  );
};

export const toModelInputsFromTable = (
  ids: string[],
  inputs: ModelInput[],
  columnIdMap: Map<string, ColumnDefinition>,
): ModelInput[] => {
  const existingById = new Map((inputs ?? []).map((i) => [i.referenceId, i]));

  return ids
    .map((id) => {
      const existing = existingById.get(id);
      if (existing) {
        return existing;
      }

      const col = columnIdMap.get(id);
      if (!col) {
        return null;
      }

      return {
        referenceId: col.id,
        name: col.displayName,
        isCollapsed: false,
        description: col.description || '',
        originalName: col.displayName,
      } as ModelInput;
    })
    .filter((input): input is ModelInput => !!input);
};

export const toModelIntermediatesFromTable = (
  ids: string[],
  columnIdMap: Map<string, ColumnDefinition>,
): ReferencedOutputItemV1[] =>
  ids
    .map((id) => columnIdMap.get(id))
    .filter((c): c is ColumnDefinition => !!c)
    .map((column) => ({
      referenceId: column.id,
      name: column.displayName,
      isOutput: false,
    }));

/**
 * Given a set of output columns, recursively finds all input columns and intermediate columns (those that are neither
 * inputs nor outputs) and sets the model state accordingly.
 *
 * @param outputColumns - The columns that are considered outputs in the model.
 * @param inputs - The current list of model input features.
 * @param editorType - The type of editor being used (e.g., scaling or tool).
 */
export const getModelInputsAndIntermediatesFromTable = (
  outputColumns: ColumnDefinition[],
  inputs: ModelInput[],
  editorType: EditorType,
) => {
  const modelType = MODEL_TYPES.SCALING;
  const tableDefinitionStore = getTableDefinitionStore(editorType);
  if (!outputColumns?.length) {
    return;
  }

  const columnIdMap = new Map<string, ColumnDefinition>(tableDefinitionStore.columns.map((c) => [c.id, c]));
  const outputIds = outputColumns.map((c) => c.id);

  const inputIds: string[] = [];
  const intermediateIds: string[] = [];
  outputColumns.forEach((col) => {
    getInputAndIntermediateIds(col.id as string, [], inputIds, intermediateIds, outputIds, columnIdMap);
  });

  setModel(modelType, {
    inputFeatures: toModelInputsFromTable(inputIds, inputs, columnIdMap),
    derivedFeatures: [
      ...outputColumns,
      ...toModelIntermediatesFromTable(intermediateIds, columnIdMap),
    ] as ReferencedOutputItemV1[],
  });
};

export const hasCollapsedInputs = (inputs: ModelInput[]): boolean => inputs.some((i) => !i.isCollapsed);

export const hasExpandedInputs = (inputs: ModelInput[]): boolean => inputs.some((i) => i.isCollapsed);

const updateAllInputsCollapse = (inputs: ModelInput[], collapsed: boolean, modelType: ModelType) => {
  const updated = inputs.map((i) => ({ ...i, isCollapsed: collapsed }));
  setModel(modelType, { inputFeatures: updated });
};

export const expandAllInputs = (inputs: ModelInput[], modelType: ModelType) =>
  updateAllInputsCollapse(inputs, false, modelType);

export const collapseAllInputs = (inputs: ModelInput[], modelType: ModelType) =>
  updateAllInputsCollapse(inputs, true, modelType);

/** Validates that a column can be used as a model output. Only columns with rules of type 'formulaCreator' are
 *  supported in the backend, for now. */
export const isValidOutputColumn = (column: ColumnDefinition): boolean => {
  const validRules = [SeeqNames.MaterializedTables.Rules.FormulaCreator];

  return column.rules.every((rule) =>
    validRules.includes(rule.rule as typeof SeeqNames.MaterializedTables.Rules.FormulaCreator),
  );
};

/**
 * Converts the features of a model into options to be display in the Formula Item Table
 *
 * @param {DerivedFeatureV1[]} features - The features to convert to options
 * @param {boolean} isOutput - True if the features are outputs, false if intermediates
 * @param {ModelInput[]} inputFeatures - Model input features
 * @param {ColumnDefinition[]} tableColumns - Optional table columns
 * @param {boolean} useIdForKey - True to use the ID as the option key, false to use the name
 * @return {FormulaItemTableOption[]} The options for the formula item table
 */
export const displayModelFeatures = (
  features: DerivedFeatureV1[],
  isOutput: boolean,
  inputFeatures: ModelInput[],
  tableColumns?: ColumnDefinition[],
  useIdForKey: boolean | undefined = false,
): FormulaItemTableOption[] =>
  features
    .filter(
      (feature) =>
        feature.isOutput === isOutput &&
        feature.id !== undefined &&
        (!('displayName' in feature) || 'showInIntermediates' in feature || 'featureId' in feature),
    )
    .map((f) => {
      const allFormulas = (f.rules ?? []).map((rule) => {
        let formula = '';
        let parameters: { name: string; identifier: string; item?: { id?: string } }[] = [];

        if (
          rule &&
          typeof rule === 'object' &&
          'arguments' in rule &&
          rule.arguments &&
          typeof rule.arguments === 'object' &&
          'formula' in rule.arguments &&
          'parameters' in rule.arguments &&
          'inputs' in rule
        ) {
          formula = (rule.arguments.formula as string) ?? '';

          const paramStr = rule.arguments.parameters as string;
          const paramArray = paramStr ? paramStr.split('|') : [];
          const ruleInputs: string[] = Array.isArray(rule.inputs)
            ? rule.inputs.map((input) => (typeof input === 'string' ? input : ''))
            : [];

          parameters = ruleInputs.map((input: string, idx: number) => ({
            name: tableColumns ? tableColumns.find((col) => col.id === input)?.displayName || input : input,
            identifier: paramArray[idx] ?? '',
            item: undefined,
          }));
        } else if (rule?.formulaCreator?.formula?.argument) {
          formula = String(rule.formulaCreator.formula.argument);

          const paramArgs = Array.isArray(rule.formulaCreator.parameters?.argument)
            ? rule.formulaCreator.parameters.argument
            : [];
          const inputs = Array.isArray(rule.inputs) ? rule.inputs : [];

          parameters = inputs.map((input: ModelRuleInputV1Object, idx: number) => {
            const matchingInputFeature = inputFeatures
              ? inputFeatures.find((feature) => feature.id === input.inputFeatureId)
              : undefined;
            return {
              name: matchingInputFeature?.name ?? input.name,
              identifier: paramArgs[idx] ?? '',
              item: matchingInputFeature ? { id: matchingInputFeature.inputItem } : undefined,
            };
          });
        }
        return { formula, parameters };
      });

      return {
        key: useIdForKey ? (f.id ?? '') : f.name,
        display: 'columnName' in f ? String(f.columnName) : String(f.name),
        allFormulas,
      };
    });

/** Validates a formula and its parameters for a one off model application.
 *
 * @param {string} formula - the formula to validate
 * @param {Array<{name: string; identifier: string; item?: {id?: string}}>} parameters - the parameters used in the formula
 */
export const validateApplication = (
  formula: string,
  parameters: { name: string; identifier: string; item?: { id?: string } }[],
) => {
  // If the ID is missing, this is for an intermediate and there is no real item yet
  const missingId = parameters.some((param) => !param.item?.id);
  if (missingId) {
    return { formula, parameters };
  }
  return validateFormula(formula, parameters as unknown as FormulaEditorParam[])
    .then(() => {
      return { formula, parameters };
    })
    .catch((formulaErrors) => {
      return { formula, parameters, formulaErrors };
    });
};

/** Validates all formulas in all options of a Formula Item Table. If any fallback formulas are valid, clears errors
 * for that option.
 * @param {FormulaItemTableOption[]} options - the formula item table options to validate
 */
export async function validateAllOptionsFormulas(options: FormulaItemTableOption[]): Promise<boolean> {
  let formulaErrorsExist = false;
  for (const option of options) {
    let optionHasErrors = false;
    if (option.allFormulas && Array.isArray(option.allFormulas)) {
      for (const formula of option.allFormulas) {
        try {
          const result = await exposedForTesting.validateApplication(formula.formula, formula.parameters);
          if ('formulaErrors' in result && result.formulaErrors) {
            formula.formulaErrors = result.formulaErrors;
            optionHasErrors = true;
          } else {
            optionHasErrors = false;
            break;
          }
        } catch (error) {
          optionHasErrors = true;
          errorToast({ httpResponseOrError: error });
        }
      }
    }
    if (optionHasErrors) {
      formulaErrorsExist = true;
    } else {
      option.allFormulas?.length &&
        option.allFormulas.forEach((formula) => {
          formula.formulaErrors = undefined;
        });
    }
  }
  return formulaErrorsExist;
}

/** Updates a specific input in the model's input features list
 *
 * @param patch - the fields to update in the input
 * @param input - the input to update
 * @param modelType - the type of model being updated (Scaling or Tool)
 */
export const updateInput = (patch: Partial<ModelInput>, input: ModelInput, modelType: ModelType) => {
  const idToUse = input.referenceId ?? input.id;
  if (!idToUse) {
    return;
  }
  // Makes sure we are using the most up-to-date inputs from the store
  const inputFeatures = getModelInputFeatures(modelType);

  let updated = false;
  const updatedFeatures = inputFeatures.map((input) => {
    const inputId = input.referenceId ?? input.id;
    if (!updated && inputId === idToUse) {
      updated = true;
      return { ...input, ...patch };
    }
    return input;
  });

  setModel(modelType, { inputFeatures: updatedFeatures });
};

export const renameToUniqueDerivedFeatureNames = (columns: ColumnDefinition[], modelType: ModelType) => {
  const derivedFeatures = _.cloneDeep(sqScalingModelStore.derivedFeatures as ReferencedOutputItemV1[]);
  const newColumnNames = findUniqueColumnNames(
    columns.map((column) => column.displayName),
    derivedFeatures.map((feature) => feature.name),
  );
  newColumnNames.forEach((newName, index) => {
    derivedFeatures[index].name = newName;
  });

  setModel(modelType, { derivedFeatures });
};

/** Validates that none of the derived feature names already exist in the scaling table column names.
 * If any do, throws an error listing the duplicates.
 *
 * @param scalingTableColumnNames - the existing column names in the scaling table
 * @param derivedFeatureNames - the names of the derived features in the model
 */
export const validateModelOutputNames = (scalingTableColumnNames: string[], derivedFeatureNames: string[]) => {
  const outputNamesInTable = getDuplicateOutputNames(scalingTableColumnNames, derivedFeatureNames);
  if (outputNamesInTable.length) {
    const listOfNames = outputNamesInTable.map((name) => `"${name}"`).join(', ');
    const namesPlural = outputNamesInTable.length > 1 ? 'names' : 'name';
    const colsPlural = outputNamesInTable.length > 1 ? 'columns' : 'column';

    throw new Error(
      `Applying this model would create duplicate column ${namesPlural}: ${listOfNames}. ` +
        `Please rename the conflicting table ${colsPlural} and try again.`,
    );
  }
};

/**
 * Returns a list of duplicate output names that exist in both the scaling table column names and the derived feature names.
 *
 * @param {string[]} scalingTableColumnNames - The existing column names in the scaling table.
 * @param {string[]} derivedFeatureNames - The names of the derived features in the model.
 * @returns {string[]} An array of duplicate output names found in both lists.
 */
export const getDuplicateOutputNames = (scalingTableColumnNames: string[], derivedFeatureNames: string[]) => {
  const outputNamesInTable = Array.from(
    new Set(derivedFeatureNames.filter((name) => scalingTableColumnNames.includes(name))),
  );

  return [...outputNamesInTable];
};

/** Fetches all items from the details pane of the supported types, decorates them, and returns the calculated
 *  outputs */
export const getAllDetailsPaneOutputs = (): NewlyAddedDerivedFeature[] => {
  const items = getAllItems({
    excludeDataStatus: [ITEM_DATA_STATUS.REDACTED],
    itemTypes: [ITEM_TYPES.CONDITION, ITEM_TYPES.SERIES, ITEM_TYPES.SCALAR],
  }).filter((item) => item.calculationType !== undefined && item.calculationType !== 'periodic-condition');

  const decoratedItems = decorate({ items });
  const outputs = Array.isArray(decoratedItems) ? decoratedItems : [decoratedItems];

  return outputs.map((output) => ({
    id: output.id,
    name: output.name,
    isOutput: true,
  }));
};

/** Fetches all items of the specified types, decorates them, and adds any new ones
 *  to the model's derived features list.
 *
 * @param derivedFeatures - The current list of derived features in the model
 * @param setOutputs - Function to update the model's derived features in state
 * @param outputs - The current list of derived features in state
 * */
export function setAllDetailsPaneItemsInModel(
  derivedFeatures: ModelOutput[],
  setOutputs: (output: ModelOutput[]) => void,
  outputs: ModelOutput[],
) {
  const outputsToAdd = getAllDetailsPaneOutputs() as ModelOutput[];
  const updatedOutputs = Array.from(new Map([...outputs, ...outputsToAdd].map((o) => [o.id, o])).values());
  const updatedDerivedFeatures = Array.from(
    new Map([...derivedFeatures, ...outputsToAdd].map((f) => [f.id, f])).values(),
  );

  // TODO: CRAB-56765: get rid of setting outputs in state separately from setting the model and just use setModel
  //  with the newly added outputs and inputs. Call getModelToolInputsAndIntermediatesFromTable to get the full list.
  if (outputsToAdd.length) {
    setOutputs(updatedOutputs);
    setModel(MODEL_TYPES.TOOL, { derivedFeatures: updatedDerivedFeatures });
  }
}

const toModelIntermediatesFromItems = (items: ModelInput[]) =>
  items.map((item) => ({
    referenceId: item.id,
    name: item.name,
    isOutput: false,
  }));

/** Collects inputs and their parent IDs from a list of dependencies. */
export function collectInputsAndParents(dependencies: OutputDependencyItem[]): {
  inputs: ModelInput[];
  parentIds: Set<string>;
} {
  const parentIds = new Set<string>();
  const inputsById = new Map<string, ModelInput>();

  dependencies.forEach((dep) => {
    if (!dep?.id) return;

    if (!inputsById.has(dep.id)) {
      const lastAncestorName = dep.ancestors?.length ? dep.ancestors[dep.ancestors.length - 1].name : null;

      inputsById.set(dep.id, {
        name: dep.name,
        originalName: dep.name,
        id: dep.id,
        inputType: dep.type,
        description: '',
        parent: lastAncestorName,
        isCollapsed: false,
        referenceId: dep.id,
      } as ModelInput);
    }

    dep.parameterOf?.forEach((param) => {
      if (param?.id) {
        parentIds.add(param.id);
      }
    });
  });

  return { inputs: Array.from(inputsById.values()), parentIds };
}

/** Given a set of model outputs, fetches their dependencies and finds all input and intermediate items. */
export const fetchModelToolInputsAndIntermediates = async (
  outputs: ModelOutput[] | NewlyAddedDerivedFeature[],
  inputs: ModelInput[],
) => {
  const outputIds = outputs.map((output) => output.id);
  const dependencyLists = await Promise.all(
    outputIds.map(async (outputId) => {
      const deps = await getDependencies({ id: outputId as string });
      return deps?.dependencies ?? [];
    }),
  );

  const { inputs: updatedInputs, parentIds } = collectInputsAndParents(dependencyLists.flat());

  const validInputs = updatedInputs
    .filter((input) => input?.id && !parentIds.has(input.id))
    .filter((input) => input.inputType !== SeeqNames.Types.CalculatedCondition);

  const newInputs: ModelInput[] = validInputs.filter((validInput) => {
    return (
      !!validInput.id &&
      !inputs.some((input) => input.id === validInput.id) &&
      !outputs.some((output) => output.id === validInput.id)
    );
  });

  const existingInputs: ModelInput[] = inputs.filter(
    (input) =>
      validInputs.some((validInput) => validInput.id === input.id) && !outputs.some((output) => output.id === input.id),
  );

  const intermediates = updatedInputs.filter(
    (input) =>
      input.inputType !== SeeqNames.Types.StoredSignal &&
      input.inputType !== SeeqNames.Types.CalculatedScalar &&
      !outputIds.includes(input.id),
  );

  return {
    inputFeatures: [...existingInputs, ...newInputs],
    derivedFeatures: [...outputs, ...toModelIntermediatesFromItems(intermediates)] as ModelOutput[],
  };
};

/** Given a set of model outputs, fetches their dependencies and finds all input and intermediate items.
 *  Sets the model state accordingly.
 *
 *  @param outputs - The current model outputs to derive inputs and intermediates from
 *  @param inputs - The current model inputs to avoid duplicates
 *  */
export const getModelToolInputsAndIntermediates = async (outputs: ModelOutput[], inputs: ModelInput[]) => {
  const updatedFeatures = await fetchModelToolInputsAndIntermediates(outputs, inputs);
  setModel(MODEL_TYPES.TOOL, updatedFeatures);
};

/**
 * Creates a new model in the backend from a model definition derived from items.
 */
export const createModelFromItems = async (model: ModelFromIdsInputV1) => {
  const { data: modelOutput } = await sqModelsApi.createModelFromItems(model);
  return modelOutput;
};

export const getInputIcon = (type: string | undefined) => {
  switch (type) {
    case SeeqNames.Types.StoredCondition:
      return "<i class='fc fc-capsule-set sq-icon-color ml3'></i>";
    case SeeqNames.Types.StoredSignal:
      return "<i class='fc fc-series sq-icon-color ml3'></i>";
    case SeeqNames.Types.CalculatedScalar:
      return "<i class='fc fc-scalar sq-icon-color ml3'></i>";
    default:
      return '';
  }
};

export const toReferencedOutputItems = (derivedFeatures: ModelOutput[]): ReferencedOutputItemV1[] => {
  return derivedFeatures.map((feature) => {
    if ('id' in feature && 'name' in feature) {
      return {
        referenceId: feature.id,
        name: feature.name,
        isOutput: true, // Selected details pane items are always outputs
      } as ReferencedOutputItemV1;
    }
    // For intermediates, they have already been formatted correctly
    return feature;
  }) as ReferencedOutputItemV1[];
};

export const executeModelFromItemsCreation = async (
  description: string,
  inputFeatures: ModelInput[],
  derivedFeatures: ModelOutput[],
  workbookId: string,
  name: string,
) => {
  try {
    await createModelFromItems({
      name,
      description,
      referencedInputItems: inputFeatures.map((i) => ({
        name: i.name,
        description: i.description,
        referenceId: i.referenceId!,
      })),
      referencedOutputItems: toReferencedOutputItems(derivedFeatures as ModelOutput[]),
      scopedTo: workbookId,
    });

    await fetchModels([workbookId]);
    successToast({ messageKey: 'MODELS.CREATE_MESSAGE' });
    resetModel(MODEL_TYPES.TOOL);
    setActiveTool(TREND_TOOLS.MODELS);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * Creates a model from items and immediately publishes it as v1 (locked).
 * Used when Enterprise users with Locking choose "Create and Publish".
 */
export const executeModelFromItemsCreationAndPublish = async (
  description: string,
  inputFeatures: ModelInput[],
  derivedFeatures: ModelOutput[],
  workbookId: string,
  name: string,
) => {
  try {
    const modelOutput = await createModelFromItems({
      name,
      description,
      referencedInputItems: inputFeatures.map((i) => ({
        name: i.name,
        description: i.description,
        referenceId: i.referenceId!,
      })),
      referencedOutputItems: toReferencedOutputItems(derivedFeatures as ModelOutput[]),
      scopedTo: workbookId,
    });

    // Publish the newly created model as v1
    if (modelOutput.versionId) {
      await sqModelsApi.publishModelVersion({ versionId: modelOutput.versionId }, { modelId: modelOutput.id });
    } else {
      console.warn('Model created without a versionId — publish step skipped. Model will remain as a draft.');
    }

    await fetchModels([workbookId]);
    successToast({ messageKey: 'MODELS.CREATE_MESSAGE' });
    resetModel(MODEL_TYPES.TOOL);
    setActiveTool(TREND_TOOLS.MODELS);
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

export const getValidColumnType = (outputType: OutputTypeEnum | undefined) => {
  switch (outputType) {
    case OutputTypeEnum.ITEMID:
      return ColumnTypeEnum.UUID;
    case OutputTypeEnum.NUMERIC:
      return ColumnTypeEnum.NUMERIC;
    case OutputTypeEnum.TEXT:
      return ColumnTypeEnum.TEXT;
    default:
      return null;
  }
};

export const resetModelApplicationAndEdit = (editorType: EditorType) => {
  setIsApplyingModel(editorType, false);
  setColumnIdToBeUsedAsModelInput(editorType, undefined);
  setModelInputToSelect(editorType, undefined);
  setColumnToEditId(editorType, undefined);
  setShowColumnProperties(editorType, false);
  setSelectedColumns(editorType, []);
};

/** Adds a newly created input column to the scaling table and updates the corresponding input feature in the model's
 *  input features list.
 */
export const updateNewlyCreatedModelInput = async (
  editorType: EditorType,
  tableDefinitionId: string,
  columnDefinition: ColumnDefinitionInputV1,
  modelInputToSelect: ModelToSelect | undefined,
  inputFeatures: ModelInput[],
) => {
  try {
    const output = await addOrUpdateColumn(editorType, tableDefinitionId, columnDefinition, undefined);
    if (output) {
      const colDefs = output.tableDefinition.columnDefinitions;
      const addedColumn = colDefs[colDefs?.length - 1];
      if (!addedColumn) {
        return;
      }

      setColumnIdToBeUsedAsModelInput(editorType, addedColumn.id);

      if (modelInputToSelect) {
        const foundColumn = colDefs.find((column: { id: string }) => column.id === addedColumn.id);
        const inputFeatureToUpdate = inputFeatures.find((input) => input.id === modelInputToSelect.id);

        if (foundColumn && inputFeatureToUpdate) {
          updateInput(
            {
              columnDefinition: {
                ...foundColumn,
                displayProperty: SeeqNames.Properties.Name,
              } as ColumnDefinition,
            },
            inputFeatureToUpdate,
            MODEL_TYPES.SCALING,
          );
        }
      }

      setSelectedColumnOption(editorType, TABLE_DEFINITION_COLUMN_OPTIONS.APPLY_MODEL);
    }
  } catch (err) {
    errorToast({ httpResponseOrError: err });
  }
};

/**
 * Applies a selected model once and creates the derived features as calculated items.
 */
export const applyModelOnce = async (modelId: string, input: ModelInitialApplicationInputV1) => {
  const { data: modelOutput } = await sqModelsApi.applyModel(input, { modelId });
  return modelOutput;
};

/** Adds items created from applying a model to the trend. */
export const addModelItemsToTrend = async (outputs: InstanceOutputMappingV1[]) => {
  const outputIds = outputs.map((output) => output.outputId);

  const items = await Promise.all(
    outputIds.map(async (id) => {
      if (!id) {
        return;
      }
      const { data: itemOutput } = await sqItemsApi.getItemAndAllProperties({ id });
      return itemOutput;
    }),
  );

  for (const item of items) {
    if (item && item.properties) {
      const instanceId = getModelInstanceIdFromProps(item.properties);
      if (instanceId) {
        const props = await getModelProps(instanceId);
        await addTrendItem(item, props);
      }
    }
  }
};

/**
 * Searches properties for one matching 'Created By Model Instance' and returns the value
 */
export const getModelInstanceIdFromProps = (
  properties: { name?: string; value?: string }[] | undefined,
  includeRetired?: boolean,
) => {
  if (properties) {
    const createdByModelInstanceProp = properties.find(
      (property) => property.name === SeeqNames.Properties.CreatedByModelInstance,
    );
    const retired = properties.find((property) => property.name === SeeqNames.Properties.InstanceRetired);
    if (createdByModelInstanceProp && (!retired || includeRetired)) {
      return createdByModelInstanceProp.value;
    }
  }
};

export const getInstanceById = async (instanceId: string): Promise<InstanceOutputV1> => {
  const { data: instance } = await sqModelsApi.getInstance({ instanceId });
  return instance;
};

export const reapplyModel = async (modelId: string, instanceId: string, input: ModelReapplicationInputV1) => {
  const { data: modelOutput } = await sqModelsApi.reapplyModel(input, { modelId, instanceId });
  return modelOutput;
};

/** Applies a model once using the specified input features and adds the resulting items to the details pane. */
export const executeSingleModelApplication = async (
  modelId: string,
  inputFeatures: ModelInput[],
  workbookId: string,
  instanceToEditId: string | undefined,
  instanceName: string,
) => {
  try {
    const inputMappings: ModelApplicationMappingV1[] = inputFeatures.map((input) => {
      const isInstanceName = input.name === SeeqNames.Models.InstanceNameInputFeatureName;
      const instanceNameTextInput = instanceName !== '' ? instanceName : input?.textInput;
      return {
        featureId: input.id,
        textInput: isInstanceName ? instanceNameTextInput : undefined,
        itemIdInput: isInstanceName ? undefined : input.inputItem,
      };
    });

    if (!instanceToEditId) {
      const result = await exposedForTesting.applyModelOnce(modelId, {
        inputs: inputMappings,
        sourceId: workbookId,
        datasourceId: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceId,
        datasourceClass: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceClass,
      } as ModelInitialApplicationInputV1);

      if (!result) {
        return;
      }

      await exposedForTesting.addModelItemsToTrend(result.outputs as InstanceOutputMappingV1[]);
      successToast({ messageKey: 'MODELS.APPLY_ONCE_MESSAGE' });
    } else {
      const result = await exposedForTesting.reapplyModel(modelId, instanceToEditId, {
        inputs: inputMappings,
        sourceId: workbookId,
        datasourceId: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceId,
        datasourceClass: SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceClass,
      } as ModelReapplicationInputV1);

      await exposedForTesting.addModelItemsToTrend(result.outputs as InstanceOutputMappingV1[]);
      successToast({ messageKey: 'MODELS.REAPPLY_MESSAGE' });
    }
    resetModel(MODEL_TYPES.TOOL);
    closeInvestigationTool();
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/** Retires the instance on the backend and clears out local trend properties related to models */
export const retireInstance = async (instanceId: string, modelType: ModelType): Promise<void> => {
  const { data: instance } = await sqModelsApi.getInstance({ instanceId });
  await sqModelsApi.retireInstance({ instanceId });
  if (instance.outputs) {
    const itemsPromises = instance.outputs
      .map((output) => {
        if (output.outputId) {
          return sqItemsApi.getItemAndAllProperties({ id: output.outputId });
        }
        return undefined;
      })
      .filter((promise) => !!promise);
    const itemsData = await Promise.all(itemsPromises);
    itemsData.forEach((itemData) => {
      if (itemData) {
        clearModelProps(itemData.data.id);
      }
    });
  }
  resetModel(modelType);
};

/** Retires a model table instance */
export const retireTableInstance = async (
  applicationId: string,
  modelType: ModelType,
  shouldDeleteColumns = false,
): Promise<void> => {
  await sqModelsApi.retireApplication({ applicationId, deleteColumns: shouldDeleteColumns });
  resetModel(modelType);
  await clearRetiredModelsProps({ onlyScalingItems: true });
};

export const retireTableInstanceAndResetTable = async (
  applicationId: string | undefined,
  tableDefinitionId: string,
  shouldDeleteColumns?: boolean,
) => {
  const editorType = EDITOR_TYPES.SCALING;
  const modelType = MODEL_TYPES.SCALING;
  if (applicationId) {
    await exposedForTesting.retireTableInstance(applicationId, modelType, shouldDeleteColumns);
    await getExistingTableDefinition(tableDefinitionId, editorType);
    setDoTableReload(editorType, true);
    setModelInputToSelect(editorType, undefined);
    setIsApplyingModel(editorType, false);
  }
};

/** Given a list of model applications, fetches all input features and returns a list of their mapped column IDs. */
export const fetchInputFeaturesFromTableApplication = async (modelColumns: ModelTableApplicationsOutputListV1) => {
  const applications = modelColumns?.applications ?? [];
  const ids = new Set<string>();
  for (const application of applications) {
    const model = await fetchModel(application.modelGuid!, MODEL_TYPES.SCALING, false);
    if (model?.inputFeatures) {
      model.inputFeatures.forEach((feature) => {
        const mapping = application.mappings!.find((mapping) => mapping.featureId === feature.id);
        if (mapping?.columnId) {
          ids.add(mapping.columnId);
        }
      });
    }
  }
  return Array.from(ids);
};

/**
 * Given a list of column definitions and a list of model applications, returns a new list of column definitions
 * where columns that are outputs of a model application are grouped under that model's name.
 *
 * @param agColumnDefs - The list of column definitions to group
 * @param applications - The list of model applications to use for grouping
 * @param inputFeatureColumnIds - The list of column IDs that are input features (these should not be grouped)
 * @returns A new list of column definitions with model output columns grouped under their respective models
 */
export function getModelColumnsDefs(
  agColumnDefs: ColDef[],
  applications: ModelTableApplicationsOutputV1[],
  inputFeatureColumnIds: string[],
): (ColDef | ColGroupDef)[] {
  const groups: ColGroupDef[] = applications.map((app) => {
    const children = agColumnDefs.filter(
      (colDef) =>
        app.mappings?.some((m) => m.columnId === colDef.field) &&
        !inputFeatureColumnIds.includes(colDef.field as string),
    );

    return {
      headerName: app.modelName,
      marryChildren: true,
      children,
    } as ColGroupDef;
  });

  const addedGroups = new Map<any, true>();
  const allColumns: (ColDef | ColGroupDef)[] = [];

  // Find the group that contains a given column field
  const findGroupForField = (field?: string) =>
    field ? groups.find((g) => (g.children as ColDef[]).some((child) => child.field === field)) : undefined;

  // Iterate through the original column definitions and add them to the new list, grouping if the column belongs to a model
  // and hasn't already been added
  agColumnDefs.forEach((colDef) => {
    const group = findGroupForField(colDef.field as string);

    if (group) {
      if (!addedGroups.has(group)) {
        addedGroups.set(group, true);
        allColumns.push(group);
      }
    } else {
      allColumns.push(colDef);
    }
  });

  return allColumns;
}

/**
 * Duplicates a given model by creating a new name, scoping it to the current workbook, and then
 * returning the result.
 *
 * @param originalModel - The model to duplicate (only id and name are needed)
 * @param workbookId - The ID of the workbook to scope the duplicate to
 * @returns The newly created model
 */
export const duplicateModel = async (
  originalModel: { id: string; name: string },
  workbookId: string,
): Promise<ModelOutputV1> => {
  const duplicateName = t('MODELS.DUPLICATED_MODEL_NAME', { originalModelName: originalModel.name });
  const duplicateResult = await sqModelsApi.duplicateModel(
    { name: duplicateName, scopedTo: workbookId },
    { modelId: originalModel.id },
  );
  return duplicateResult.data;
};

export const exposedForTesting = {
  validateApplication,
  applyModelOnce,
  reapplyModel,
  addModelItemsToTrend,
  retireTableInstance,
};
